import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Steps } from 'core/components';
import { StepsProvider, useNotificationContext } from 'core/states';
import { usePartBomActions } from 'partBoms/hooks';
import { IPartBom } from 'partBoms/types';
import { UpsertTaxonomyGroup } from 'taxonomy/components';
import { Color, Size } from 'theme/enums';

import { UpsertPartBomStep } from './enums';
import { Details } from './steps';
import { IUpsertPartBomForm } from './types';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmitSuccess?: (partBom: IPartBom) => void;
  partBom?: IPartBom;
  level?: number;
  fullScreen?: boolean;
}

export const UpsertPartBom = ({
  isOpen,
  onClose,
  onSubmitSuccess,
  partBom,
  level = 0,
  fullScreen = false,
}: IProps) => {
  const [formValues, setFormValues] = useState<IUpsertPartBomForm | null>(null);

  const detailsRef = useRef(null);
  const taxonomyRef = useRef(null);

  const { t } = useTranslation();

  const { clearNotification } = useNotificationContext();

  const { upsertPartBom } = usePartBomActions();

  const isEdit = useMemo(() => !!partBom?.id, [partBom?.id]);

  const defaultValues = useMemo(() => {
    return {
      number: partBom?.number,
      description: partBom?.descriptionLong,
      uom: partBom?.uom,
      bomType: partBom?.bomType,
      ...formValues,
    };
  }, [partBom, formValues]);

  const onDetailsSubmit = useCallback(async () => {
    try {
      const formResult = await detailsRef.current.triggerFormSubmit();

      setFormValues({ ...formValues, ...formResult });

      return true;
    } catch {
      return false;
    }
  }, [formValues]);

  const onTaxonomyBack = useCallback(() => {
    const taxonomyFields = taxonomyRef.current.getFormValues();

    setFormValues({ ...formValues, ...taxonomyFields });
  }, [formValues]);

  const getFormData = useCallback(async () => {
    let data = { ...formValues };

    const taxonomyFields = await taxonomyRef.current.triggerFormSubmit();

    data = { ...data, ...taxonomyFields };

    setFormValues(data);

    return data;
  }, [formValues]);

  const handleOnClose = useCallback(() => {
    onClose();
    setFormValues(null);
    clearNotification();
  }, [onClose, clearNotification]);

  const onSubmit = useCallback(async () => {
    let data = null;

    try {
      data = await getFormData();
    } catch {
      return false;
    }

    const { success, part: updatedPartBom } = await upsertPartBom({
      part: {
        ...(data.sourcePartBom ? { sourcePartBomId: data.sourcePartBom.id } : {}),
        ...(isEdit && { id: partBom.id }),
        ...(!isEdit && { number: data.number }),
        description: data.description,
        descriptionLong: data.description,
        uom: data.uom,
        bomType: data.bomType,
        taxonomyGroupId: data.taxonomy?.id,
      },
    });

    if (!success) {
      return false;
    }

    setTimeout(() => {
      onSubmitSuccess?.(updatedPartBom);
      handleOnClose();
    }, 1000);

    return true;
  }, [upsertPartBom, partBom?.id, isEdit, getFormData, onSubmitSuccess, handleOnClose]);

  const steps = useMemo(() => {
    return [
      {
        type: UpsertPartBomStep.DETAILS,
        title: t('titleAddPartBomDetails'),
        content: (
          <Details
            ref={detailsRef}
            onClose={onClose}
            defaultValues={defaultValues}
            isEdit={!!partBom?.id}
          />
        ),
        nextButton: {
          options: { onClick: onDetailsSubmit },
        },
      },
      {
        type: UpsertPartBomStep.TAXONOMY,
        title: t('titleAddPartBomTaxonomy'),
        content: (
          <UpsertTaxonomyGroup
            ref={taxonomyRef}
            defaultTaxonomyPath={formValues?.taxonomy || partBom?.taxonomyPath}
          />
        ),
        previousButton: {
          options: { onClick: onTaxonomyBack },
        },
        nextButton: {
          text: t('buttonLabelSave'),
          options: { onClick: onSubmit },
        },
      },
    ];
  }, [
    t,
    onClose,
    defaultValues,
    partBom?.id,
    partBom?.taxonomyPath,
    onDetailsSubmit,
    onSubmit,
    formValues,
    onTaxonomyBack,
  ]);

  return (
    <StepsProvider steps={steps} onClose={handleOnClose}>
      <Steps
        title={partBom?.id ? t('titleEditPartBom') : t('titleAddPartBom')}
        isOpen={isOpen}
        color={Color.NEUTRAL}
        size={Size.MD}
        fullWidth={false}
        fullScreen={fullScreen}
        interactive
        level={level}
      />
    </StepsProvider>
  );
};
